import {useCallback, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import DownloadIcon from '@mui/icons-material/Download';
import {Box, Grid} from '@mui/material';

import {InfoIcon} from '../../../../../../assets/employee_satisfaction_bar';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import {EmpionTooltip} from '../../../../../../components/EmpionTooltip/EmpionTooltip';
import {CompanyContext} from '../../../../../../contexts/CompanyContext/CompanyContext';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useDownloadPdfProfile} from '../../../../../../hooks/useDownloadPdfProfile';
import {useTrackingEvents} from '../../../../../../hooks/useTrackingEvents';
import {orangeColors} from '../../../../../../layouts/theme/designSystem';
import {MatchData} from '../../../../../../types/shared-recruiting-types';
import {TrackingEvents} from '../../../../../../types/TrackingEvents';

type Props = {
  companyMatchDetails: MatchData;
};

export const ContactInformationPdfExport = ({companyMatchDetails}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.cardDetails'});
  const {t: tr} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);
  const {Version, Action, Category} = TrackingEvents;
  const {eventAttributes} = useTrackingEvents({
    version: Version.v1,
    action: Action.UserDownloadedPdf,
    category: Category.ApplicantPdf,
    payload: {},
  });

  const applicant = companyMatchDetails?.applicant;
  const isTalenSurveyDone = companyMatchDetails.culturalFitScore !== null;
  const {company} = useContext(CompanyContext);

  const {downloadPdf, isLoading: isPdfDownloading} = useDownloadPdfProfile({
    companyId: company?.data?.resourceId || '',
    matchId: companyMatchDetails.resourceId,
    fileName: `${applicant?.personalInformation.firstName}_${applicant?.personalInformation.lastName}_profile.pdf`,
    onError: () => dispatchMessage(t('noPdfData'), 'error'),
  });

  const handlePdfExport = useCallback(async () => {
    await downloadPdf();
  }, [downloadPdf]);

  return (
    <Grid container alignItems='center' gap={1} justifyItems={'center'}>
      <EmpionTooltip title={tr('missingCultureDetails.tooltip')} isEnabled={!isTalenSurveyDone}>
        <Box display='inline-block'>
          <EmpionButton
            {...eventAttributes}
            onClick={handlePdfExport}
            variant='cta'
            sx={{
              minHeight: '32px',
              mt: 1,
            }}
            startIcon={
              !isTalenSurveyDone ? (
                <InfoIcon fill={orangeColors[900]} height={23} width={23} />
              ) : (
                <DownloadIcon sx={{mr: '4px', opacity: isPdfDownloading ? 0.5 : 1}} />
              )
            }
            disabled={!isTalenSurveyDone || isPdfDownloading}
          >
            {t('exportProfile')}
          </EmpionButton>
        </Box>
      </EmpionTooltip>
    </Grid>
  );
};
